import {
    useEffect,
    type FC
} from "react";
import {
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IAvatarGroupProps from "./type";
import type {
    AvatarGroupMoreTriggerProps,
    AvatarGroupMoreProps,
    AvatarGroupAddProps,
    AvatarGroupSlotType
} from "./type";
import stylesheet, {
    getAvatarGroupMembersLabel,
    getAvatarGroupMoreTexts,
    getAvatarGroupAddStyle,
    getAvatarGroupSlots,
    getAvatarGroupSize,
    getAvatarGroupMax,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePressScale
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    Plus as PlusIcon
} from "lucide-react-native";
import {
    getAvatarRingColor,
    getAvatarSizeKey,
    getAvatarColors
} from "../avatar/stylesheet";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    formatNumber
} from "../../helpers/locale/format";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    warnOnce
} from "../../utils/development";
import StateLayer from "../stateLayer";
import Tooltip from "../tooltip";
import Avatar from "../avatar";
import Text from "../text";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const AvatarGroupMoreTrigger: FC<AvatarGroupMoreTriggerProps> = ({
    onLongPress,
    children,
    onFocus,
    onPress,
    onBlur,
    label,
    style
}) => <Pressable
    onLongPress={onLongPress}
    aria-label={label}
    onFocus={onFocus}
    onPress={onPress}
    onBlur={onBlur}
    style={style}
    role="img"
>
    {children}
</Pressable>;

const AvatarGroupMore: FC<AvatarGroupMoreProps> = ({
    customLocalize,
    hiddenAvatars,
    isActionable,
    currentSize,
    customTheme,
    focusStyle,
    moreColors,
    moreStyle,
    slotStyle,
    count
}) => {
    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState();

    const texts = getAvatarGroupMoreTexts({
        locale: activeLocale,
        hiddenAvatars,
        localize
    });

    const renderCount = () => <Text
        customColor={moreColors.titleColor}
        variant={currentSize.fontSize}
        style={stylesheet.moreTitle}
        customTheme={customTheme}
        numberOfLines={1}
    >
        {`+${formatNumber({
            locale: activeLocale,
            value: count
        })}`}
    </Text>;

    if(isActionable) {
        return <View
            style={[
                stylesheet.more,
                moreStyle,
                slotStyle
            ]}
            aria-hidden={true}
        >
            {renderCount()}
        </View>;
    }

    return <Tooltip
        style={[
            slotStyle,
            isFocusVisible ? focusStyle : undefined
        ]}
        customLocalize={customLocalize}
        customTheme={customTheme}
        title={texts.title}
        side="top"
    >
        <AvatarGroupMoreTrigger
            onFocus={interactionHandlers.onFocus}
            onBlur={interactionHandlers.onBlur}
            style={[
                stylesheet.more,
                moreStyle,
                getFocusRingStyle({
                    colorKey: "default",
                    isFocusVisible,
                    borders,
                    colors
                })
            ]}
            label={texts.label}
            onPress={undefined}
        >
            {renderCount()}
        </AvatarGroupMoreTrigger>
    </Tooltip>;
};

const AvatarGroupAdd: FC<AvatarGroupAddProps> = ({
    customLocalize,
    currentSize,
    customTheme,
    focusStyle,
    isDisabled,
    fillColor,
    ringColor,
    slotStyle,
    onPress
}) => {
    const {
        touchTargets,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled
    });

    const pressScaleStyle = usePressScale({
        customTheme,
        isDisabled,
        isPressed
    });

    const hitArea = getHitArea({
        visualSize: currentSize.size,
        touchTargets,
        pointerType
    });

    const addDynamicStyle = getAvatarGroupAddStyle({
        isFocusVisible,
        currentSize,
        isDisabled,
        fillColor,
        ringColor,
        radiuses,
        borders,
        colors
    });

    return <AnimatedPressable
        {...interactionHandlers}
        hitSlop={Math.max(0, (hitArea.minHeight - currentSize.size) / 2)}
        accessibilityLabel={localize("avatar-group-add")}
        style={[
            slotStyle,
            stylesheet.add,
            addDynamicStyle,
            isFocusVisible ? focusStyle : undefined,
            pressScaleStyle
        ]}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        onPress={onPress}
        role="button"
    >
        <StateLayer
            borderRadius={radiuses.full}
            customTheme={customTheme}
            isHovered={isHovered}
            isPressed={isPressed}
        />
        <PlusIcon
            color={isDisabled ? colors.system.state.content.disabled.neutral : colors.content.icon.mid}
            size={currentSize.iconSize}
        />
    </AnimatedPressable>;
};

const AvatarGroup: FC<IAvatarGroupProps> = ({
    avatarBackgroundColor: avatarBackgroundColorProp,
    titleAbbreviationType = "first-last",
    isShowStatusIndicatorSplicer,
    isShowStatusIndicatorBorder,
    isStatusIndicator = false,
    isWorkWithAction = false,
    "aria-label": ariaLabel,
    accessibilityLabel,
    isDisabled = false,
    isLoading = false,
    size = "medium",
    customLocalize,
    avatars = [],
    borderColor,
    customTheme,
    onLongPress,
    accessible,
    imageProps,
    onAddPress,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    style,
    role,
    max,
    ...props
}) => {
    const {
        shapeConfigs,
        surfaceColor,
        touchTargets,
        avatarSizes,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const avatarBackgroundColor = avatarBackgroundColorProp ?? surfaceColor;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onPressOut: isLoading ? undefined : onPressOut,
        onPressIn: isLoading ? undefined : onPressIn,
        isDisabled,
        onHoverOut,
        onHoverIn,
        onFocus,
        onBlur
    });

    const isActionable = isWorkWithAction;

    const sizeKey = getAvatarSizeKey({
        size
    });

    const currentSize = getAvatarGroupSize({
        avatarSizes,
        iconSizes,
        borders,
        size
    });

    const currentMax = getAvatarGroupMax({
        max
    });

    const slots = getAvatarGroupSlots({
        hasAdd: !!onAddPress,
        max: currentMax,
        isLoading,
        avatars
    });

    const ringColor = getAvatarRingColor({
        avatarBackgroundColor,
        borderColor,
        colors
    });

    const moreColors = getAvatarColors({
        surfaceColor: avatarBackgroundColor,
        isPlaceholder: false,
        titleColor: "high",
        type: "neutral",
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        members: membersDynamicStyle,
        surface: surfaceDynamicStyle,
        focus: focusDynamicStyle,
        slots: slotDynamicStyles,
        more: moreDynamicStyle
    } = useStyles({
        slotCount: slots.length,
        isFocusVisible,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        moreColors,
        isLoading,
        ringColor,
        radiuses,
        borders,
        colors,
        spaces
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const membersLabel = getAvatarGroupMembersLabel({
        locale: activeLocale,
        isStatusIndicator,
        max: currentMax,
        localize,
        avatars
    });

    const getAccessibilityLabel = () => {
        const label = ariaLabel || accessibilityLabel;

        if(label && membersLabel) {
            return localize("avatar-group-name", [
                label,
                membersLabel
            ]);
        }

        return label || membersLabel;
    };

    const currentLabel = getAccessibilityLabel();

    const fillColor = colors.content.container[avatarBackgroundColor];

    const isCurrentShowStatusIndicatorBorder = isShowStatusIndicatorBorder ?? isShowStatusIndicatorSplicer ?? true;

    useEffect(() => {
        if(isShowStatusIndicatorSplicer === undefined) {
            return;
        }

        warnOnce({
            message: "AvatarGroup: \"isShowStatusIndicatorSplicer\" is deprecated and will be removed in the next major version. Use \"isShowStatusIndicatorBorder\" instead."
        });
    }, [
        isShowStatusIndicatorSplicer
    ]);

    useEffect(() => {
        if(sizeKey === size) {
            return;
        }

        warnOnce({
            message: `AvatarGroup: size "${size}" is deprecated and will be removed in the next major version. Use "${sizeKey}" instead, the largest step of the avatar ladder.`
        });
    }, [
        sizeKey,
        size
    ]);

    useEffect(() => {
        if(!isActionable || currentLabel) {
            return;
        }

        warnOnce({
            message: "AvatarGroup: an actionable group needs an \"accessibilityLabel\" (or \"aria-label\") or member titles so screen readers can announce it."
        });
    }, [
        isActionable,
        currentLabel
    ]);

    const renderSlot = (slot: AvatarGroupSlotType, index: number) => {
        if(slot.kind === "loading") {
            return <Avatar
                avatarBackgroundColor={avatarBackgroundColor}
                style={slotDynamicStyles[index]}
                customLocalize={customLocalize}
                key="avatar-group-loading"
                borderColor={borderColor}
                customTheme={customTheme}
                isDisabled={isDisabled}
                isShowBorder={true}
                aria-hidden={true}
                isLoading={true}
                size={sizeKey}
            />;
        }

        if(slot.kind === "add" && onAddPress) {
            return <AvatarGroupAdd
                isDisabled={isDisabled || isLoading}
                slotStyle={slotDynamicStyles[index]}
                customLocalize={customLocalize}
                focusStyle={focusDynamicStyle}
                currentSize={currentSize}
                customTheme={customTheme}
                key="avatar-group-add"
                ringColor={ringColor}
                fillColor={fillColor}
                onPress={onAddPress}
            />;
        }

        if(slot.kind === "add") {
            return null;
        }

        if(slot.kind === "more") {
            return <AvatarGroupMore
                slotStyle={slotDynamicStyles[index]}
                hiddenAvatars={slot.hiddenAvatars}
                customLocalize={customLocalize}
                focusStyle={focusDynamicStyle}
                moreStyle={moreDynamicStyle}
                isActionable={isActionable}
                currentSize={currentSize}
                customTheme={customTheme}
                key="avatar-group-more"
                moreColors={moreColors}
                count={slot.count}
            />;
        }

        return <Avatar
            titleColor="high"
            {...slot.avatar}
            isStatusIndicator={slot.avatar.badge ? false : isStatusIndicator}
            isShowStatusIndicatorBorder={isCurrentShowStatusIndicatorBorder}
            titleAbbreviationType={titleAbbreviationType}
            avatarBackgroundColor={avatarBackgroundColor}
            key={`avatar-group-avatar-${index}`}
            customLocalize={customLocalize}
            style={[
                slot.avatar.style,
                slotDynamicStyles[index]
            ]}
            borderColor={borderColor}
            customTheme={customTheme}
            isWorkWithAction={false}
            imageProps={imageProps}
            isDisabled={isDisabled}
            isShowBorder={true}
            aria-hidden={true}
            isLoading={false}
            size={sizeKey}
        />;
    };

    if(!isActionable) {
        const hasInteractiveChildren = !!onAddPress || slots.some((slot) => slot.kind === "more");

        if(!hasInteractiveChildren) {
            return <View
                {...props}
                role={role ?? (currentLabel ? "group" : undefined)}
                accessible={accessible ?? !!currentLabel}
                aria-label={currentLabel}
                style={[
                    style,
                    stylesheet.content
                ]}
                aria-busy={isLoading}
            >
                {slots.map(renderSlot)}
            </View>;
        }

        const isNative = Platform.OS !== "web";

        const memberCount = slots.filter((slot) => slot.kind === "member" || slot.kind === "loading").length;

        return <View
            {...props}
            role={role ?? (currentLabel ? "group" : undefined)}
            accessible={accessible ?? false}
            aria-label={currentLabel}
            style={[
                style,
                stylesheet.content
            ]}
            aria-busy={isLoading}
        >
            <View
                role={isNative && currentLabel ? "img" : undefined}
                aria-label={isNative ? currentLabel : undefined}
                accessible={isNative && !!currentLabel}
                style={[
                    stylesheet.content,
                    membersDynamicStyle
                ]}
            >
                {slots.slice(0, memberCount).map(renderSlot)}
            </View>
            {slots.slice(memberCount).map((slot, index) => renderSlot(slot, index + memberCount))}
        </View>;
    }

    const addSlot = slots[slots.length - 1];

    const hasAddButton = !!onAddPress && !!addSlot && addSlot.kind === "add";

    const {
        nativeID,
        testID,
        ...pressableProps
    } = props;

    const renderActionable = () => <Pressable
        {...(hasAddButton ? pressableProps : props)}
        {...interactionHandlers}
        style={[
            hasAddButton ? undefined : containerStyle,
            stylesheet.container,
            containerDynamicStyle,
            hasAddButton ? membersDynamicStyle : undefined
        ]}
        onLongPress={isLoading ? undefined : onLongPress}
        onPress={isLoading ? undefined : onPress}
        aria-disabled={isDisabled}
        aria-label={currentLabel}
        accessible={accessible}
        role={role ?? "button"}
        aria-busy={isLoading}
        disabled={isDisabled}
    >
        <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                isHovered={!isLoading && isHovered}
                isPressed={!isLoading && isPressed}
                borderRadius={radiuses.full}
                customTheme={customTheme}
            />
            {slots.filter((slot) => slot.kind !== "add").map(renderSlot)}
        </View>
    </Pressable>;

    if(!hasAddButton) {
        return renderActionable();
    }

    return <View
        style={[
            containerStyle,
            stylesheet.row
        ]}
        nativeID={nativeID}
        testID={testID}
    >
        {renderActionable()}
        {renderSlot(addSlot, slots.length - 1)}
    </View>;
};
export default AvatarGroup;
